// Modal de criação / edição de usuário (PO1-2020).
// Padrão de Modal: modal-align-top + fade={false} + div header customizado.
//
// Estrutura preparada para receber tabs adicionais nas próximas histórias:
//   - PO1-2021 Senha (admin) — tab "Senha"
//   - PO1-2022 Convite por e-mail — tab "Convite"
//   - PO1-2023 Classificação — tab "Classificação"
//   - PO1-2024 Grupos — tab "Grupos"
//
// Por enquanto apenas a tab "Informações" está ativa; outras aparecem como
// placeholders desabilitados em modo edição (e ocultas em modo criação).

import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

import { createUser, updateUser } from "services/usersApi";
import { useToast } from "hooks/toast";
import { useAuth } from "hooks/auth";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import UserGroupsTab from "./UserGroupsTab";
import UserClassificacaoTab from "./UserClassificacaoTab";
import UserInviteTab from "./UserInviteTab";
import UserPermissionsTab from "./UserPermissionsTab";

// Tabs que dependem do user já existir (id no banco) só ficam habilitadas
// em modo edição. A senha (PO1-2021) tem modal próprio, aberto pelo botão da
// linha na lista de usuários; a aba desabilitada "Senha" saiu na PO1-3296.
const TABS = [
  { id: "info", label: "Informações", enabled: true, requiresEdit: false },
  { id: "invite", label: "Convite", enabled: true, requiresEdit: true },
  // PO1-3296: Classificação e Permissões são só do admin Everlog (o backend
  // devolve 403 para os demais).
  { id: "classification", label: "Classificação", enabled: true, requiresEdit: true, adminOnly: true },
  { id: "groups", label: "Grupos", enabled: true, requiresEdit: true },
  { id: "permissions", label: "Permissões", enabled: true, requiresEdit: true, adminOnly: true },
];

// Abas visíveis conforme o modo (criação/edição) e quem está logado.
export function abasVisiveis({ isCreate, isAdmin }) {
  return TABS.filter((t) => {
    // Em modo criação, só mostra tabs que não exigem edit (i.e. o id já existir)
    if (isCreate && t.requiresEdit) return false;
    if (t.adminOnly && !isAdmin) return false;
    return t.enabled || !isCreate;
  });
}

const EMPTY_FORM = { name: "", surname: "", email: "", userName: "" };

export default function EditUserModal({ isOpen, user, onClose, onSaved }) {
  const isCreate = !user;
  const [activeTab, setActiveTab] = useState("info");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [groupsCount, setGroupsCount] = useState(0);
  const { addToast } = useToast();
  const { user: logado } = useAuth();
  const isAdmin = !!(logado && logado.setor === "admin");

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab("info");
    setErrors({});
    setGroupsCount(0);
    setForm(
      user
        ? {
            name: user.name || "",
            surname: user.surname || "",
            email: user.email || "",
            userName: user.userName || "",
          }
        : EMPTY_FORM
    );
  }, [isOpen, user]);

  const handleField = useCallback((field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Nome obrigatório";
    if (!form.userName.trim()) nextErrors.userName = "Nome de usuário obrigatório";
    if (!form.email.trim()) {
      nextErrors.email = "E-mail obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = "E-mail inválido";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [form]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim() || null,
        email: form.email.trim(),
        userName: form.userName.trim(),
      };
      const saved = isCreate
        ? await createUser(payload)
        : await updateUser(user.id, payload);
      addToast({
        place: "tr",
        type: "success",
        title: "Salvo",
        description: isCreate ? "Usuário criado" : "Usuário atualizado",
        icon: "tim-icons icon-check-2",
      });
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err) {
      console.error("[EditUserModal] save:", err);
      const status = err.response && err.response.status;
      const data = (err.response && err.response.data) || {};
      const message = data.error || "Erro ao salvar usuário";

      // 409 indica conflito de email/userName — destacar o campo.
      if (status === 409 && data.field) {
        setErrors((prev) => ({ ...prev, [data.field]: message }));
      }
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [validate, form, isCreate, user, addToast, onSaved, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      modalClassName="modal-align-top"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: AUTOPILOT_COLORS.insetSurface,
          borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          padding: "16px 20px",
        }}
      >
        <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: 15 }}>
          {isCreate ? "Novo usuário" : `Editar usuário — ${user.name}`}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: AUTOPILOT_COLORS.title,
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 4px",
          }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody>
        <Nav
          tabs
          style={{
            marginBottom: 16,
            gap: 4,
            borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          }}
        >
          {abasVisiveis({ isCreate, isAdmin }).map((t) => (
            <NavItem key={t.id}>
              <NavLink
                className={classnames({ active: activeTab === t.id })}
                onClick={() => t.enabled && setActiveTab(t.id)}
                style={{
                  cursor: t.enabled ? "pointer" : "not-allowed",
                  opacity: t.enabled ? 1 : 0.4,
                  // PO1-3296: aba ativa na cor de destaque com sublinhado; as demais
                  // em texto legível. Sobrescreve o "pill" do nav-tabs do tema, que
                  // quase não diferenciava a ativa (e sumia no modo claro).
                  color: activeTab === t.id ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.text,
                  fontWeight: activeTab === t.id ? 600 : 500,
                  background: "transparent",
                  border: "none",
                  borderRadius: 0,
                  borderBottom: `2px solid ${activeTab === t.id ? AUTOPILOT_COLORS.accent : "transparent"}`,
                  marginBottom: -1,
                  padding: "0.6rem 1rem",
                }}
                title={t.enabled ? "" : `Em breve (${t.hint})`}
              >
                {t.label}
                {t.id === "groups" && groupsCount > 0 && (
                  <span
                    style={{
                      display: "inline-block",
                      marginLeft: 6,
                      fontSize: 10,
                      background: AUTOPILOT_COLORS.accent,
                      color: AUTOPILOT_COLORS.onAccent,
                      borderRadius: 8,
                      padding: "1px 6px",
                      minWidth: 16,
                      textAlign: "center",
                    }}
                  >
                    {groupsCount}
                  </span>
                )}
                {!t.enabled && (
                  <span style={{ fontSize: 10, marginLeft: 4, color: AUTOPILOT_COLORS.muted }}>
                    {t.hint}
                  </span>
                )}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="info">
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Nome *</Label>
                  <Input
                    value={form.name}
                    onChange={handleField("name")}
                    invalid={!!errors.name}
                  />
                  {errors.name && (
                    <div style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: 12, marginTop: 4 }}>
                      {errors.name}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Sobrenome</Label>
                  <Input
                    value={form.surname}
                    onChange={handleField("surname")}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Nome de usuário *</Label>
                  <Input
                    value={form.userName}
                    onChange={handleField("userName")}
                    invalid={!!errors.userName}
                  />
                  {errors.userName && (
                    <div style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: 12, marginTop: 4 }}>
                      {errors.userName}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>E-mail *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={handleField("email")}
                    invalid={!!errors.email}
                  />
                  {errors.email && (
                    <div style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: 12, marginTop: 4 }}>
                      {errors.email}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </TabPane>
          {!isCreate && (
            <TabPane tabId="invite">
              <UserInviteTab user={user} />
            </TabPane>
          )}
          {!isCreate && isAdmin && (
            <TabPane tabId="classification">
              <UserClassificacaoTab user={user} onSaved={onSaved} />
            </TabPane>
          )}
          {!isCreate && (
            <TabPane tabId="groups">
              <UserGroupsTab
                user={user}
                onCountChange={setGroupsCount}
                onSaved={onSaved}
              />
            </TabPane>
          )}
          {!isCreate && isAdmin && (
            <TabPane tabId="permissions">
              <UserPermissionsTab user={user} />
            </TabPane>
          )}
        </TabContent>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={saving}>
          {activeTab === "info" ? "Cancelar" : "Fechar"}
        </Button>
        {activeTab === "info" && (
          <Button color="success" onClick={handleSave} disabled={saving}>
            {saving ? "Salvando…" : isCreate ? "Criar" : "Salvar"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
